// File: frontend/src/AuthContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, getApiBaseUrl } from './services/api';
import { io } from 'socket.io-client';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to get socket base URL directly derived from API base URL
  const getSocketUrl = () => {
    const apiBase = getApiBaseUrl();
    return apiBase.replace(/\/api\/?$/, '');
  };

  // Initial load from localStorage
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (token && user && user !== 'undefined' && user !== 'null') {
      try {
        const parsedUser = JSON.parse(user);
        if (parsedUser && typeof parsedUser === 'object') {
          setCurrentUser(parsedUser);
          authAPI.setToken(token);
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } catch (err) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } else {
      if (user === 'undefined' || user === 'null') {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  // Manage socket connection lifecycle based on currentUser
  useEffect(() => {
    let activeSocket = null;

    if (currentUser) {
      const token = localStorage.getItem('token');
      if (token) {
        activeSocket = io(getSocketUrl(), {
          auth: { token },
          transports: ['polling', 'websocket'],
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
        });

        activeSocket.on('connect', () => {
          console.log("Socket connection established.");
        });

        setSocket(activeSocket);
      }
    } else {
      setSocket(null);
    }

    return () => {
      if (activeSocket) {
        console.log("Disconnecting socket.");
        activeSocket.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]); // Only re-run when authenticated user ID changes

  // Set user session
  const setSession = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    authAPI.setToken(token);
    setCurrentUser(user);
  };

  // Login handler
  const login = async (username, password, isStudent = false) => {
    try { 
        const apiCall = isStudent 
            ? authAPI.studentLogin(username, password) 
            : authAPI.login(username, password);

        const response = await apiCall;
        const { token, user } = response;

        setSession(token, user);
        return { success: true };
    } catch (error) {
        return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    authAPI.setToken(null);
    setCurrentUser(null);
  };

  const value = { currentUser, socket, login, logout, setSession };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}